import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { logoutUser } from '../../store/slices/userSlice';
import { FaHouseDamage, FaAddressCard, FaSignOutAlt, FaUsers } from 'react-icons/fa';
import { MdSettingsInputComponent, MdPrivacyTip } from 'react-icons/md';
import { IoMdAnalytics } from 'react-icons/io';
import PrivacyPolicyModal from '../PrivacyPolicyModal';
import ConfirmModal from '../ConfirmModal';

interface ProfileMenuProps {
  onClose?: () => void;
}

const ProfileMenu: React.FC<ProfileMenuProps> = ({ onClose }) => {
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.user);
  const [showMenu, setShowMenu] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [profileImageError, setProfileImageError] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowMenu(false);
        onClose?.();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose]);

  useEffect(() => {
    setProfileImageError(false);
  }, [user?.avatarUrl]);

  const handleCloseMenu = () => {
    setShowMenu(false);
    onClose?.();
  };

  const handleLogout = async () => {
    setIsLoggingOut(true);
    try {
      await dispatch(logoutUser()).unwrap();
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
      navigate('/login');
    } finally {
      setIsLoggingOut(false);
      setShowLogoutConfirm(false);
    }
  };

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => setShowMenu(!showMenu)}
        className="w-9 h-9 rounded-full flex items-center justify-center transition-all duration-200 hover:opacity-80 focus:outline-none focus:ring-2 focus:ring-[#FF5924]/20"
      >
        {user?.avatarUrl && !profileImageError ? (
          <div className="w-full h-full rounded-full overflow-hidden ring-1 ring-[#F0F0F0]">
            <img
              src={user.avatarUrl}
              alt={user?.name || 'Profile'}
              className="w-full h-full object-cover"
              onError={() => setProfileImageError(true)}
            />
          </div>
        ) : (
          <div className="w-full h-full bg-[#FF5924] rounded-full flex items-center justify-center text-white font-medium text-sm">
            {user?.name?.charAt(0)?.toUpperCase() || 'U'}
          </div>
        )}
      </button>

      {showMenu && (
        <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl border border-[#F0F0F0] shadow-lg z-50 overflow-hidden">
          <div className="py-1">
            <div className="px-4 py-3 border-b border-[#F0F0F0]">
              <div className="text-[#1A1A1A] font-medium text-sm">{user?.name || 'User'}</div>
              <div className="text-[#6B6B6B] text-xs mt-0.5">{user?.email || 'user@example.com'}</div>
            </div>

            <Link
              to="/dashboard"
              onClick={handleCloseMenu}
              className="w-full flex items-center space-x-3 px-4 py-2.5 text-left hover:bg-[#FAFAFA] transition-colors"
            >
              <FaHouseDamage className="text-base text-[#FF5924]" />
              <span className="text-[#1A1A1A] text-sm">Dashboard</span>
            </Link>
            <Link
              to="/team"
              onClick={handleCloseMenu}
              className="w-full flex items-center space-x-3 px-4 py-2.5 text-left hover:bg-[#FAFAFA] transition-colors"
            >
              <FaUsers className="text-base text-[#6B6B6B]" />
              <span className="text-[#1A1A1A] text-sm">Team Mode</span>
            </Link>
            <Link
              to="/account-settings"
              onClick={handleCloseMenu}
              className="w-full flex items-center space-x-3 px-4 py-2.5 text-left hover:bg-[#FAFAFA] transition-colors"
            >
              <MdSettingsInputComponent className="text-base text-[#6B6B6B]" />
              <span className="text-[#1A1A1A] text-sm">Settings</span>
            </Link>
            <Link
              to="/worklog"
              onClick={handleCloseMenu}
              className="w-full flex items-center space-x-3 px-4 py-2.5 text-left hover:bg-[#FAFAFA] transition-colors"
            >
              <IoMdAnalytics className="text-base text-[#FF5924]" />
              <span className="text-[#1A1A1A] text-sm">Analytics</span>
            </Link>
            {/* <Link
              to="/subscription"
              onClick={handleCloseMenu}
              className="w-full flex items-center space-x-3 px-4 py-2.5 text-left hover:bg-[#FAFAFA] transition-colors"
            >
              <FaAddressCard className="text-base text-[#FF5924]" />
              <span className="text-[#1A1A1A] text-sm">Subscription</span>
            </Link> */}
            <button
              onClick={() => {
                handleCloseMenu();
                setShowPrivacyModal(true);
              }}
              className="w-full flex items-center space-x-3 px-4 py-2.5 text-left hover:bg-[#FAFAFA] transition-colors"
            >
              <MdPrivacyTip className="text-base text-[#6B6B6B]" />
              <span className="text-[#1A1A1A] text-sm">Privacy</span>
            </button>
            <div className="border-t border-[#F0F0F0] my-1"></div>
            <button
              onClick={() => {
                handleCloseMenu();
                setShowLogoutConfirm(true);
              }}
              disabled={isLoggingOut}
              className="w-full flex items-center space-x-3 px-4 py-2.5 text-left hover:bg-red-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <FaSignOutAlt className="text-base text-red-500" />
              <span className="text-[#1A1A1A] text-sm">
                {isLoggingOut ? 'Signing out...' : 'Sign Out'}
              </span>
            </button>
          </div>
        </div>
      )}

      <PrivacyPolicyModal
        isOpen={showPrivacyModal}
        onClose={() => setShowPrivacyModal(false)}
      />

      <ConfirmModal
        open={showLogoutConfirm}
        onClose={() => setShowLogoutConfirm(false)}
        onConfirm={handleLogout}
        title="Sign Out"
        message="Are you sure you want to sign out of your account?"
        confirmText="Sign Out"
        cancelText="Cancel"
        type="danger"
        preventCloseOnOutsideClick={true}
        isLoading={isLoggingOut}
      />
    </div>
  );
};

export default ProfileMenu;
